import { createContext, useContext, useEffect, useState } from 'react';
import { 
  User,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase-config';
import { BaseUser } from '../types';
import { upsertUser } from '../handlers/db';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase-config';
import PageLoader from '../components/PageLoader';

const formatAuthUser = (user: User): BaseUser => {
    return {
      uid: user.uid,
      email: user.email,
      plan: null,
      stripeCustomerId: null,
      subscriptionId: null,
      generationsLeft: 3,
      agentChatsLeft: 10,
      apiKeys: []
    };
  };

interface AuthContextType {
  currentUser: BaseUser | null;
  signInWithGoogle: () => Promise<BaseUser>;
  logout: () => Promise<void>;
  userLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<BaseUser | null>(null);
  const [userLoading, setUserLoading] = useState(true);

  async function signInWithGoogle() {
    try {
      const userCred = await signInWithPopup(auth, googleProvider);
      const user = formatAuthUser(userCred.user);
      const upsertedUser = await upsertUser(user);
      setCurrentUser(upsertedUser as BaseUser);
      return upsertedUser as BaseUser;
    } catch (error) {
      console.error('Sign in error:', error);
      // Fallback to redirect flow when popups are blocked or disallowed
      // Common error codes: auth/popup-blocked, auth/popup-closed-by-user
      const message = (error as any)?.code || '';
      if (typeof message === 'string' && message.startsWith('auth/popup')) {
        await signInWithRedirect(auth, googleProvider);
        // The redirect flow will reload the page; return a pending promise
        return new Promise<BaseUser>(() => {});
      }
      throw error;
    }
  }

  async function logout() {
    return signOut(auth);
  }

  useEffect(() => {
    let unsubscribeDoc: (() => void) | undefined;
    // Fallback in case the auth callback never fires
    const fallback = setTimeout(() => {
      setCurrentUser(null);
      setUserLoading(false);
    }, 0);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        clearTimeout(fallback);
        if (user) {
          const userRef = doc(db, `User/${user.uid}`);
          unsubscribeDoc = onSnapshot(userRef, (doc) => {
            if (doc.exists()) {
              setCurrentUser({ ...doc.data(), uid: user.uid } as BaseUser);
            } else {
              const formattedUser = formatAuthUser(user);
              upsertUser(formattedUser);
              setCurrentUser(formattedUser);
            }
            setUserLoading(false);
          });
        } else {
          setCurrentUser(null);
          setUserLoading(false);
        }
      } catch (error) {
        clearTimeout(fallback);
        console.error('Auth state change error:', error);
        setCurrentUser(null);
        setUserLoading(false);
      }
    });

    return () => {
      clearTimeout(fallback);
      unsubscribe();
      if (unsubscribeDoc) {
        unsubscribeDoc();
      }
    };
  }, []);

  if (userLoading) {
    return <PageLoader />
  }

  const value = {
    currentUser,
    signInWithGoogle,
    logout,
    userLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
